import React, { useMemo, useCallback, useState, useEffect } from "react";
import { GoogleMap, useJsApiLoader, Marker, Polyline } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import OrderMarker, {
  IOrderMarkerData,
  OrderMarkerStatus,
} from "../../general/map/order-marker";
import RoutePolyline, { RouteStatus, DashedPolyline } from "../../general/map/route-polyline";
import DistanceLabel, { calculateMidpoint, calculateDistance } from "../../general/map/distance-label";
import { MAP_DEFAULT_LOCATION, MAP_DEFAULT_ZOOM, GOOGLE_MAPS_LIBRARIES } from "../../../utils/helpers/constants";
import { ILatLng, decodePolyline, findPathMidpoint, calculateDistanceBetweenPoints, calculateRouteDistance } from "../../../utils/geo/haversine";
import { IMapPolylines, IRoutesComparison, IPolylineSegment } from "../../../models/trip/response";
import { findNearestPointOnRoute } from "../../../utils/geo/route-optimizer";
import styles from "./style.module.scss";

/** Route view mode for comparison */
export type RouteViewMode = "active" | "optimized" | "shortest" | "nearest_neighbor" | "all";

export interface IMapOrder extends IOrderMarkerData {
  location: ILatLng;
}

export interface IMapStore {
  id: number;
  name: string;
  location: ILatLng;
  logo?: string | null;
}

// Route colors configuration (must match RoutePolyline styles)
const routeColors: Record<RouteStatus, { outbound: string; return: string }> = {
  pending: { outbound: "#3458a4", return: "#7c9fd4" },
  active: { outbound: "#52c41a", return: "#13a8a8" },
  completed: { outbound: "#8c8c8c", return: "#a0a0a0" },
  preview: { outbound: "#fa8c16", return: "#d48806" },
};

// Comparison route colors (matching strategy colors in trip-details)
const comparisonColors = {
  route_optimization: { main: "#fa8c16", light: "#ffc069" },      // Orange for route optimization
  hybrid_shortest: { main: "#722ed1", light: "#b37feb" },          // Purple for hybrid shortest
  nearest_neighbor_shortest: { main: "#faad14", light: "#ffe58f" }, // Gold for nearest neighbor
};

/**
 * Get arrow icon configuration for direction indication on comparison routes
 */
const getComparisonArrowIcon = (fillColor: string): google.maps.Symbol => ({
  path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW ?? 0,
  scale: 3,
  strokeColor: "#fff",
  strokeWeight: 1,
  fillColor,
  fillOpacity: 1,
});

export interface ITripRouteMapProps {
  storeLocation?: ILatLng;
  stores?: IMapStore[];
  selectedStoreId?: number | null;
  assignedOrders: IMapOrder[];
  unassignedOrders?: IMapOrder[];
  recommendedOrderIds?: number[];
  selectedOrderId?: number | null;
  routeStatus?: RouteStatus;
  isEditable?: boolean;
  showDistanceLabels?: boolean;
  width?: string;
  height?: string;
  /** New mapPolylines structure with segments for map rendering */
  mapPolylines?: IMapPolylines | null;
  /** Distance from last order back to store (return segment) */
  returnDistance?: number | null;
  /** Route comparison data with polylines for both routes */
  routesComparison?: IRoutesComparison | null;
  /** Route view mode: active (default), optimized, shortest, or both */
  routeViewMode?: RouteViewMode;
  onStoreSelect?: (storeId: number) => void;
  onOrderSelect?: (orderId: number) => void;
  onOrderAdd?: (orderId: number) => void;
  onOrderRemove?: (orderId: number) => void;
  onMapLoad?: (map: google.maps.Map) => void;
}

// Store marker: purple with shop icon (matching ShopOutlined)
const STORE_MARKER_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg width="60" height="75" viewBox="-6 -6 60 75" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
      </filter>
    </defs>
    <path d="M24 2C10.75 2 2 10.75 2 24C2 42 24 62 24 62S46 42 46 24C46 10.75 37.25 2 24 2Z"
          fill="#722ed1"
          stroke="#531dab"
          stroke-width="2"
          filter="url(#shadow)"/>
    <circle cx="24" cy="22" r="12" fill="white"/>
    <g fill="#722ed1">
      <path d="M15 16h18v2.5c0 1.5-1.5 2.5-3 2.5s-3-1-3-2.5c0 1.5-1.5 2.5-3 2.5s-3-1-3-2.5c0 1.5-1.5 2.5-3 2.5s-3-1-3-2.5V16z"/>
      <rect x="16" y="21" width="16" height="7" rx="0.5"/>
      <rect x="22" y="23" width="4" height="5" fill="white" rx="0.5"/>
    </g>
  </svg>
`)}`;

// Selected store marker: larger and blue with shop icon (matching ShopOutlined)
const SELECTED_STORE_MARKER_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg width="70" height="88" viewBox="-7 -7 70 88" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="shadow-sel" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow dx="0" dy="3" stdDeviation="3" flood-opacity="0.4"/>
      </filter>
    </defs>
    <path d="M28 2C12.54 2 2 12.54 2 28C2 49 28 73 28 73S54 49 54 28C54 12.54 43.46 2 28 2Z"
          fill="#1890ff"
          stroke="#096dd9"
          stroke-width="2"
          filter="url(#shadow-sel)"/>
    <circle cx="28" cy="25" r="14" fill="white"/>
    <g fill="#1890ff">
      <path d="M17 18h22v3c0 1.8-1.8 3-3.7 3s-3.6-1.2-3.6-3c0 1.8-1.8 3-3.7 3s-3.7-1.2-3.7-3c0 1.8-1.8 3-3.6 3s-3.7-1.2-3.7-3V18z"/>
      <rect x="18" y="24" width="20" height="9" rx="0.5"/>
      <rect x="25" y="26" width="6" height="7" fill="white" rx="0.5"/>
    </g>
  </svg>
`)}`;

const TripRouteMap: React.FC<ITripRouteMapProps> = ({
  storeLocation,
  stores = [],
  selectedStoreId = null,
  assignedOrders,
  unassignedOrders = [],
  recommendedOrderIds = [],
  selectedOrderId = null,
  routeStatus = "pending",
  isEditable = true,
  showDistanceLabels = true,
  width = "100%",
  height = "100%",
  mapPolylines,
  returnDistance,
  routesComparison,
  routeViewMode = "active",
  onStoreSelect,
  onOrderSelect,
  onOrderAdd,
  onOrderRemove,
  onMapLoad: onMapLoadProp,
}) => {
  const { t } = useTranslation();
  const [showInfoWindowId, setShowInfoWindowId] = useState<number | null>(null);
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_KEY}`,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Calculate initial center based on available data
  const initialCenter = useMemo<ILatLng>(() => {
    // Prefer stores array, fallback to storeLocation
    if (stores.length > 0 && stores[0].location) return stores[0].location;
    if (storeLocation) return storeLocation;
    // If no store, try to center on first assigned order
    const firstAssigned = assignedOrders.find(o => o.location);
    if (firstAssigned?.location) return firstAssigned.location;
    // If no assigned, try first unassigned
    const firstUnassigned = unassignedOrders.find(o => o.location);
    if (firstUnassigned?.location) return firstUnassigned.location;
    // Fallback to default
    return MAP_DEFAULT_LOCATION;
  }, [stores, storeLocation, assignedOrders, unassignedOrders]);

  const sortedAssignedOrders = useMemo(
    () => [...assignedOrders].sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
    [assignedOrders]
  );

  const routePath = useMemo(() => {
    const path: ILatLng[] = [];
    // Prefer stores array, fallback to storeLocation
    const startingStore = stores.length > 0 && stores[0].location
      ? stores[0].location
      : storeLocation;

    if (startingStore) {
      path.push(startingStore);
    }
    sortedAssignedOrders.forEach((order) => {
      if (order.location) {
        path.push(order.location);
      }
    });
    // Add return to store (round trip)
    if (startingStore && sortedAssignedOrders.length > 0) {
      path.push(startingStore);
    }
    return path;
  }, [stores, storeLocation, sortedAssignedOrders]);

  // Decode polyline segments from mapPolylines
  const decodedRoutePath = useMemo(() => {
    if (!isLoaded || !mapPolylines?.segments?.length) {
      return routePath; // fallback to direct lines from order locations
    }

    const allPoints: ILatLng[] = [];
    const sortedSegments = [...mapPolylines.segments].sort((a, b) => a.index - b.index);

    sortedSegments.forEach((segment) => {
      if (!segment.polyline) return;
      const decoded = decodePolyline(segment.polyline);
      decoded.forEach((point, idx) => {
        // Avoid duplicates at segment boundaries
        if (allPoints.length === 0 || idx > 0) {
          allPoints.push(point);
        }
      });
    });

    return allPoints.length > 0 ? allPoints : routePath;
  }, [mapPolylines, routePath, isLoaded]);

  // Split route into outbound (store to all orders) and return (last order back to store) segments
  const { outboundPath, returnPath } = useMemo(() => {
    // When we have mapPolylines segments, the last segment is the return segment
    if (mapPolylines?.segments?.length && mapPolylines.segments.length > 1) {
      const sortedSegments = [...mapPolylines.segments].sort((a, b) => a.index - b.index);
      const numSegments = sortedSegments.length;

      // Build outbound from all segments except the last
      const outboundPoints: ILatLng[] = [];
      for (let i = 0; i < numSegments - 1; i++) {
        const segment = sortedSegments[i];
        if (segment.polyline) {
          const decoded = decodePolyline(segment.polyline);
          decoded.forEach((point, idx) => {
            if (outboundPoints.length === 0 || idx > 0) {
              outboundPoints.push(point);
            }
          });
        }
      }

      // Last segment is the return segment
      const lastSegment = sortedSegments[numSegments - 1];
      const returnPoints = lastSegment.polyline ? decodePolyline(lastSegment.polyline) : [];

      return {
        outboundPath: outboundPoints.length > 0 ? outboundPoints : decodedRoutePath,
        returnPath: returnPoints
      };
    }

    // Fallback: split based on last order location
    if (decodedRoutePath.length < 2 || sortedAssignedOrders.length === 0) {
      return { outboundPath: decodedRoutePath, returnPath: [] as ILatLng[] };
    }

    const startingStore = stores.length > 0 && stores[0].location
      ? stores[0].location
      : storeLocation;

    if (!startingStore) {
      return { outboundPath: decodedRoutePath, returnPath: [] as ILatLng[] };
    }

    // Find the last order location
    const lastOrder = sortedAssignedOrders[sortedAssignedOrders.length - 1];
    if (!lastOrder?.location) {
      return { outboundPath: decodedRoutePath, returnPath: [] as ILatLng[] };
    }

    // Find the index in decodedRoutePath closest to the last order
    let lastOrderPathIndex = -1;
    let minDistance = Infinity;
    const threshold = 0.001; // ~100m threshold

    for (let i = decodedRoutePath.length - 1; i >= 0; i--) {
      const point = decodedRoutePath[i];
      const dist = Math.abs(point.lat - lastOrder.location.lat) + Math.abs(point.lng - lastOrder.location.lng);
      if (dist < threshold && dist < minDistance) {
        minDistance = dist;
        lastOrderPathIndex = i;
        break;
      }
    }

    // If we didn't find within threshold, find closest point
    if (lastOrderPathIndex === -1) {
      for (let i = 0; i < decodedRoutePath.length; i++) {
        const point = decodedRoutePath[i];
        const dist = Math.abs(point.lat - lastOrder.location.lat) + Math.abs(point.lng - lastOrder.location.lng);
        if (dist < minDistance) {
          minDistance = dist;
          lastOrderPathIndex = i;
        }
      }
    }

    // If we found a point near the last order, split there
    if (lastOrderPathIndex > 0 && lastOrderPathIndex < decodedRoutePath.length - 1) {
      const outbound = decodedRoutePath.slice(0, lastOrderPathIndex + 1);
      const returnSeg = decodedRoutePath.slice(lastOrderPathIndex);
      return { outboundPath: outbound, returnPath: returnSeg };
    }

    return { outboundPath: decodedRoutePath, returnPath: [] as ILatLng[] };
  }, [decodedRoutePath, sortedAssignedOrders, stores, storeLocation, mapPolylines]);

  // Helper to extract lat/lng from various location formats
  const extractLatLng = useCallback((location: any): ILatLng | null => {
    if (!location) return null;
    // Direct lat/lng properties
    if (typeof location.lat === 'number' && typeof location.lng === 'number') {
      return { lat: location.lat, lng: location.lng };
    }
    // GeoJSON coordinates array [lng, lat]
    if (Array.isArray(location.coordinates) && location.coordinates.length >= 2) {
      return { lat: location.coordinates[1], lng: location.coordinates[0] };
    }
    return null;
  }, []);

  // Decode individual segments for distance label positioning
  const decodedSegmentPaths = useMemo(() => {
    if (!mapPolylines?.segments?.length || !isLoaded) {
      return null;
    }

    const sortedSegments = [...mapPolylines.segments].sort((a, b) => a.index - b.index);

    return sortedSegments.map((segment) => {
      if (!segment.polyline) {
        return [];
      }
      return decodePolyline(segment.polyline);
    });
  }, [mapPolylines, isLoaded]);

  // Decode comparison route polylines (all 3 strategies)
  const comparisonPaths = useMemo(() => {
    if (!isLoaded || !routesComparison) {
      return {
        route_optimization: [] as ILatLng[],
        hybrid_shortest: [] as ILatLng[],
        nearest_neighbor_shortest: [] as ILatLng[]
      };
    }

    const decodeRouteSegments = (polylines: IPolylineSegment[] | undefined): ILatLng[] => {
      if (!polylines?.length) return [];

      const allPoints: ILatLng[] = [];
      const sortedSegments = [...polylines].sort((a, b) => a.index - b.index);

      sortedSegments.forEach((segment) => {
        if (!segment.polyline) return;
        const decoded = decodePolyline(segment.polyline);
        decoded.forEach((point, idx) => {
          // Avoid duplicates at segment boundaries
          if (allPoints.length === 0 || idx > 0) {
            allPoints.push(point);
          }
        });
      });

      return allPoints;
    };

    // New format: get polylines from strategies object
    if (routesComparison.comparisonEnabled && routesComparison.strategies) {
      return {
        route_optimization: decodeRouteSegments(routesComparison.strategies.route_optimization?.polylines),
        hybrid_shortest: decodeRouteSegments(routesComparison.strategies.hybrid_shortest?.polylines),
        nearest_neighbor_shortest: decodeRouteSegments(routesComparison.strategies.nearest_neighbor_shortest?.polylines),
      };
    }

    // Legacy format: only 2 routes
    return {
      route_optimization: decodeRouteSegments(routesComparison.originalRoute?.polylines),
      hybrid_shortest: decodeRouteSegments(routesComparison.shortestRoute?.polylines),
      nearest_neighbor_shortest: [] as ILatLng[],
    };
  }, [routesComparison, isLoaded]);

  // Decode individual comparison route segments for distance label positioning
  const comparisonSegmentPaths = useMemo(() => {
    if (!isLoaded || !routesComparison) {
      return {
        route_optimization: [] as ILatLng[][],
        hybrid_shortest: [] as ILatLng[][],
        nearest_neighbor_shortest: [] as ILatLng[][]
      };
    }

    const decodeSegmentsIndividually = (polylines: IPolylineSegment[] | undefined): ILatLng[][] => {
      if (!polylines?.length) return [];

      const sortedSegments = [...polylines].sort((a, b) => a.index - b.index);
      return sortedSegments.map((segment) => {
        if (!segment.polyline) return [];
        return decodePolyline(segment.polyline);
      });
    };

    // New format: get polylines from strategies object
    if (routesComparison.comparisonEnabled && routesComparison.strategies) {
      return {
        route_optimization: decodeSegmentsIndividually(routesComparison.strategies.route_optimization?.polylines),
        hybrid_shortest: decodeSegmentsIndividually(routesComparison.strategies.hybrid_shortest?.polylines),
        nearest_neighbor_shortest: decodeSegmentsIndividually(routesComparison.strategies.nearest_neighbor_shortest?.polylines),
      };
    }

    // Legacy format: only 2 routes
    return {
      route_optimization: decodeSegmentsIndividually(routesComparison.originalRoute?.polylines),
      hybrid_shortest: decodeSegmentsIndividually(routesComparison.shortestRoute?.polylines),
      nearest_neighbor_shortest: [] as ILatLng[][],
    };
  }, [routesComparison, isLoaded]);

  // Check if comparison mode is active
  const isComparisonMode = routeViewMode !== "active" && routesComparison;

  // Determine which routes to show based on viewMode
  const showOptimizedRoute = isComparisonMode && (routeViewMode === "optimized" || routeViewMode === "all");
  const showShortestRoute = isComparisonMode && (routeViewMode === "shortest" || routeViewMode === "all");
  const showNearestNeighborRoute = isComparisonMode && (routeViewMode === "nearest_neighbor" || routeViewMode === "all");
  const showActiveRoute = routeViewMode === "active" || !isComparisonMode;

  // Calculate waypoint distances for labels
  const waypointDistances = useMemo(() => {
    if (!showDistanceLabels || !isLoaded) {
      return [];
    }

    const distances: Array<{
      position: google.maps.LatLngLiteral;
      distance: number;
      isReturn: boolean;
      label: string;
    }> = [];

    // Get starting store location - handle both formats
    let startingStore: ILatLng | null = null;
    if (stores.length > 0 && stores[0].location) {
      startingStore = extractLatLng(stores[0].location);
    }
    if (!startingStore && storeLocation) {
      startingStore = extractLatLng(storeLocation);
    }

    // Build waypoints: store -> order1 -> order2 -> ...
    const waypoints: ILatLng[] = [];
    if (startingStore) {
      waypoints.push(startingStore);
    }
    sortedAssignedOrders.forEach((order) => {
      const orderLoc = extractLatLng(order.location);
      if (orderLoc) {
        waypoints.push(orderLoc);
      }
    });

    // Need at least 2 waypoints to calculate distances
    if (waypoints.length < 2) {
      return [];
    }

    // If we have decoded segment paths, use them for accurate label positioning
    if (decodedSegmentPaths && decodedSegmentPaths.length > 0) {
      const numSegments = decodedSegmentPaths.length;
      const numOutboundSegments = numSegments > 1 ? numSegments - 1 : numSegments;

      // Outbound segments (all except the last segment which is the return)
      for (let i = 0; i < numOutboundSegments; i++) {
        const path = decodedSegmentPaths[i];
        if (!path || path.length < 2) continue;

        // Find midpoint ON the actual route path
        const midpoint = findPathMidpoint(path);
        if (!midpoint) continue;

        // Calculate distance: prefer API data, then route distance, then straight-line
        let distance: number;
        if (i > 0 && i <= sortedAssignedOrders.length) {
          const prevOrder = sortedAssignedOrders[i - 1] as any;
          // Use pivot.distanceToNext if available (API calculated), otherwise calculate from path
          distance = prevOrder?.pivot?.distanceToNext ?? calculateRouteDistance(path);
        } else {
          // First segment (store to first order): calculate actual route distance
          distance = calculateRouteDistance(path);
        }

        distances.push({
          position: midpoint,
          distance,
          isReturn: false,
          label: `${i + 1}`,
        });
      }

      // Return segment (last segment)
      if (numSegments > 1) {
        const returnPath = decodedSegmentPaths[numSegments - 1];
        if (returnPath && returnPath.length >= 2) {
          const returnMidpoint = findPathMidpoint(returnPath);
          if (returnMidpoint) {
            // Use provided returnDistance or calculate from path
            const returnDist = returnDistance ?? calculateRouteDistance(returnPath);
            distances.push({
              position: returnMidpoint,
              distance: returnDist,
              isReturn: true,
              label: t("return"),
            });
          }
        }
      }

      // Safeguard: if we got fewer distances than expected, add missing ones with fallback
      const expectedOutbound = sortedAssignedOrders.length > 0 ? sortedAssignedOrders.length : 0;
      if (distances.filter(d => !d.isReturn).length < expectedOutbound && waypoints.length >= 2) {
        // We're missing some outbound segments, fill in with straight-line
        const existingCount = distances.filter(d => !d.isReturn).length;
        for (let i = existingCount; i < waypoints.length - 1; i++) {
          const from = waypoints[i];
          const to = waypoints[i + 1];
          let distance: number;
          if (i > 0) {
            const prevOrder = sortedAssignedOrders[i - 1] as any;
            distance = prevOrder?.pivot?.distanceToNext ?? calculateDistance(from, to);
          } else {
            distance = calculateDistance(from, to);
          }
          distances.push({
            position: calculateMidpoint(from, to),
            distance,
            isReturn: false,
            label: `${i + 1}`,
          });
        }
      }

      // Safeguard: add return segment if not already added
      const hasReturn = distances.some(d => d.isReturn);
      if (!hasReturn && startingStore && waypoints.length > 1) {
        const lastOrderLoc = waypoints[waypoints.length - 1];
        const returnDist = returnDistance ?? calculateDistance(lastOrderLoc, startingStore);
        distances.push({
          position: calculateMidpoint(lastOrderLoc, startingStore),
          distance: returnDist,
          isReturn: true,
          label: t("return"),
        });
      }
    } else {
      // Fallback: use straight-line midpoints when no segment paths available
      // Calculate distance between each consecutive waypoint (outbound segments)
      for (let i = 0; i < waypoints.length - 1; i++) {
        const from = waypoints[i];
        const to = waypoints[i + 1];

        // Try to use distanceToNext from pivot if available (more accurate from API)
        let distance: number;
        if (i > 0) {
          const prevOrder = sortedAssignedOrders[i - 1] as any;
          distance = prevOrder?.pivot?.distanceToNext ?? calculateDistance(from, to);
        } else {
          // First segment (store to first order) - always calculate
          distance = calculateDistance(from, to);
        }

        const midpoint = calculateMidpoint(from, to);
        distances.push({
          position: midpoint,
          distance,
          isReturn: false,
          label: `${i + 1}`,
        });
      }

      // Add return segment distance (last order back to store)
      if (startingStore && waypoints.length > 1) {
        const lastOrderLoc = waypoints[waypoints.length - 1];
        const returnDist = returnDistance ?? calculateDistance(lastOrderLoc, startingStore);
        const returnMidpoint = calculateMidpoint(lastOrderLoc, startingStore);

        distances.push({
          position: returnMidpoint,
          distance: returnDist,
          isReturn: true,
          label: t("return"),
        });
      }
    }

    return distances;
  }, [showDistanceLabels, sortedAssignedOrders, stores, storeLocation, returnDistance, t, extractLatLng, decodedSegmentPaths, isLoaded]);

  // Calculate distance labels for comparison routes (all 3 strategies)
  const comparisonDistanceLabels = useMemo(() => {
    type LabelType = Array<{ position: google.maps.LatLngLiteral; distance: number; segmentIndex: number }>;
    if (!showDistanceLabels || !isLoaded || !routesComparison) {
      return {
        route_optimization: [] as LabelType,
        hybrid_shortest: [] as LabelType,
        nearest_neighbor_shortest: [] as LabelType
      };
    }

    const calculateLabelsForRoute = (
      segmentPaths: ILatLng[][],
      polylines: IPolylineSegment[] | undefined
    ): LabelType => {
      if (!segmentPaths.length || !polylines?.length) return [];

      const labels: LabelType = [];
      const sortedPolylines = [...polylines].sort((a, b) => a.index - b.index);

      segmentPaths.forEach((path, index) => {
        if (path.length < 2) return;

        // Find midpoint on the actual route path
        const midpoint = findPathMidpoint(path);
        if (!midpoint) return;

        // Get distance from polyline segment (in meters, convert to km)
        const segmentData = sortedPolylines[index];
        const distanceKm = segmentData?.distanceMeters
          ? segmentData.distanceMeters / 1000
          : calculateRouteDistance(path);

        labels.push({
          position: midpoint,
          distance: distanceKm,
          segmentIndex: index,
        });
      });

      return labels;
    };

    // New format: get polylines from strategies object
    if (routesComparison.comparisonEnabled && routesComparison.strategies) {
      return {
        route_optimization: calculateLabelsForRoute(
          comparisonSegmentPaths.route_optimization,
          routesComparison.strategies.route_optimization?.polylines
        ),
        hybrid_shortest: calculateLabelsForRoute(
          comparisonSegmentPaths.hybrid_shortest,
          routesComparison.strategies.hybrid_shortest?.polylines
        ),
        nearest_neighbor_shortest: calculateLabelsForRoute(
          comparisonSegmentPaths.nearest_neighbor_shortest,
          routesComparison.strategies.nearest_neighbor_shortest?.polylines
        ),
      };
    }

    // Legacy format: only 2 routes
    return {
      route_optimization: calculateLabelsForRoute(
        comparisonSegmentPaths.route_optimization,
        routesComparison.originalRoute?.polylines
      ),
      hybrid_shortest: calculateLabelsForRoute(
        comparisonSegmentPaths.hybrid_shortest,
        routesComparison.shortestRoute?.polylines
      ),
      nearest_neighbor_shortest: [] as LabelType,
    };
  }, [showDistanceLabels, isLoaded, routesComparison, comparisonSegmentPaths]);

  const mapBounds = useMemo(() => {
    if (!isLoaded) return null;

    const allLocations: ILatLng[] = [];

    // Include all store locations from stores array
    stores.forEach((store) => {
      if (store.location) {
        allLocations.push(store.location);
      }
    });

    // Fallback to storeLocation if stores array is empty
    if (stores.length === 0 && storeLocation) {
      allLocations.push(storeLocation);
    }

    assignedOrders.forEach((order) => {
      if (order.location) {
        allLocations.push(order.location);
      }
    });

    unassignedOrders.forEach((order) => {
      if (order.location) {
        allLocations.push(order.location);
      }
    });

    if (allLocations.length === 0) return null;

    const bounds = new window.google.maps.LatLngBounds();
    allLocations.forEach((loc) => {
      bounds.extend({ lat: loc.lat, lng: loc.lng });
    });

    return bounds;
  }, [isLoaded, stores, storeLocation, assignedOrders, unassignedOrders]);

  // Fit bounds when orders change
  useEffect(() => {
    if (mapRef && mapBounds) {
      mapRef.fitBounds(mapBounds, 50);
    }
  }, [mapRef, mapBounds]);

  // Pan to selected order when it changes
  useEffect(() => {
    if (selectedOrderId && mapRef) {
      const order = [...assignedOrders, ...unassignedOrders].find(
        (o) => o.id === selectedOrderId
      );
      if (order?.location) {
        mapRef.panTo({ lat: order.location.lat, lng: order.location.lng });
      }
    }
  }, [selectedOrderId, assignedOrders, unassignedOrders, mapRef]);

  // Pan to selected store when it changes
  useEffect(() => {
    if (selectedStoreId && mapRef) {
      const store = stores.find((s) => s.id === selectedStoreId);
      if (store?.location) {
        mapRef.panTo({ lat: store.location.lat, lng: store.location.lng });
      }
    }
  }, [selectedStoreId, stores, mapRef]);

  const handleStoreSelect = useCallback(
    (storeId: number) => {
      onStoreSelect?.(storeId);
    },
    [onStoreSelect]
  );

  const handleOrderSelect = useCallback(
    (orderId: number) => {
      setShowInfoWindowId(orderId);
      onOrderSelect?.(orderId);
    },
    [onOrderSelect]
  );

  const handleCloseInfoWindow = useCallback(() => {
    setShowInfoWindowId(null);
  }, []);

  // Close InfoWindow when clicking on the map (outside markers)
  const handleMapClick = useCallback(() => {
    setShowInfoWindowId(null);
  }, []);

  const handleOrderAdd = useCallback(
    (orderId: number) => {
      onOrderAdd?.(orderId);
      setShowInfoWindowId(null);
    },
    [onOrderAdd]
  );

  const handleOrderRemove = useCallback(
    (orderId: number) => {
      onOrderRemove?.(orderId);
      setShowInfoWindowId(null);
    },
    [onOrderRemove]
  );

  // Calculate distances to route and connection points for unassigned orders
  const unassignedOrdersWithRouteDistance = useMemo(() => {
    if (decodedRoutePath.length < 2) {
      // No route available, return orders without distance info
      return unassignedOrders.map(order => ({
        ...order,
        distanceToRoute: undefined,
        nearestPointOnRoute: undefined,
      }));
    }

    return unassignedOrders.map(order => {
      if (!order.location) {
        return {
          ...order,
          distanceToRoute: undefined,
          nearestPointOnRoute: undefined,
        };
      }

      try {
        const result = findNearestPointOnRoute(order.location, decodedRoutePath);
        return {
          ...order,
          distanceToRoute: result.distanceToRoute,
          nearestPointOnRoute: result.nearestPointOnRoute,
        };
      } catch (error) {
        console.error("Error calculating distance to route:", error);
        return {
          ...order,
          distanceToRoute: undefined,
          nearestPointOnRoute: undefined,
        };
      }
    });
  }, [unassignedOrders, decodedRoutePath]);

  const getMarkerStatus = useCallback(
    (order: IMapOrder, isAssigned: boolean): OrderMarkerStatus => {
      if (selectedOrderId === order.id) return "selected";
      if (isAssigned) return "assigned";
      if (recommendedOrderIds.includes(order.id)) return "recommended";
      return "unassigned";
    },
    [selectedOrderId, recommendedOrderIds]
  );

  const containerStyle = useMemo(
    () => ({
      width: "100%",
      height: "100%",
      minHeight: "500px",
    }),
    []
  );

  const mapOptions = useMemo(
    () => ({
      disableDefaultUI: false,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: true,
      fullscreenControl: true,
      clickableIcons: false,
      gestureHandling: "cooperative",
    }),
    []
  );

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      setMapRef(map);
      if (mapBounds) {
        map.fitBounds(mapBounds, 50);
      }
      // Notify parent component about map load
      onMapLoadProp?.(map);
    },
    [mapBounds, onMapLoadProp]
  );

  if (!isLoaded) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.mapContainer}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={initialCenter}
        zoom={MAP_DEFAULT_ZOOM}
        options={mapOptions}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
      >
        {/* Active Route - Outbound Polyline (store to last order) */}
        {showActiveRoute && outboundPath.length >= 2 && (
          <RoutePolyline
            path={outboundPath}
            status={routeStatus}
            segmentType="outbound"
            showArrows
            zIndex={2}
          />
        )}

        {/* Active Route - Return Polyline (last order back to store) */}
        {showActiveRoute && returnPath.length >= 2 && (
          <RoutePolyline
            path={returnPath}
            status={routeStatus}
            segmentType="return"
            showArrows
            zIndex={1}
          />
        )}

        {/* Comparison: Route Optimization - Orange with arrows */}
        {showOptimizedRoute && comparisonPaths.route_optimization.length >= 2 && (
          <Polyline
            path={comparisonPaths.route_optimization}
            options={{
              strokeColor: comparisonColors.route_optimization.main,
              strokeWeight: routeViewMode === "all" ? 4 : 5,
              strokeOpacity: 0.9,
              zIndex: routeViewMode === "all" ? 4 : 2,
              icons: [
                {
                  icon: getComparisonArrowIcon(comparisonColors.route_optimization.main),
                  offset: "100%",
                  repeat: "80px",
                },
              ],
            }}
          />
        )}

        {/* Comparison: Hybrid Shortest - Purple with arrows */}
        {showShortestRoute && comparisonPaths.hybrid_shortest.length >= 2 && (
          <Polyline
            path={comparisonPaths.hybrid_shortest}
            options={{
              strokeColor: comparisonColors.hybrid_shortest.main,
              strokeWeight: routeViewMode === "all" ? 4 : 5,
              strokeOpacity: 0.9,
              zIndex: routeViewMode === "all" ? 3 : 2,
              icons: [
                {
                  icon: getComparisonArrowIcon(comparisonColors.hybrid_shortest.main),
                  offset: "100%",
                  repeat: "80px",
                },
              ],
            }}
          />
        )}

        {/* Comparison: Nearest Neighbor Shortest - Gold with arrows */}
        {showNearestNeighborRoute && comparisonPaths.nearest_neighbor_shortest.length >= 2 && (
          <Polyline
            path={comparisonPaths.nearest_neighbor_shortest}
            options={{
              strokeColor: comparisonColors.nearest_neighbor_shortest.main,
              strokeWeight: routeViewMode === "all" ? 4 : 5,
              strokeOpacity: 0.9,
              zIndex: routeViewMode === "all" ? 2 : 2,
              icons: [
                {
                  icon: getComparisonArrowIcon(comparisonColors.nearest_neighbor_shortest.main),
                  offset: "100%",
                  repeat: "80px",
                },
              ],
            }}
          />
        )}

        {/* Distance Labels between waypoints (only in active mode) */}
        {showActiveRoute && showDistanceLabels && waypointDistances.map((item, index) => (
          <DistanceLabel
            key={`distance-${index}`}
            position={item.position}
            distance={item.distance}
            isReturnSegment={item.isReturn}
            segmentIndex={index}
            backgroundColor={item.isReturn ? routeColors[routeStatus].return : routeColors[routeStatus].outbound}
          />
        ))}

        {/* Distance Labels for Route Optimization (comparison mode) */}
        {showOptimizedRoute && showDistanceLabels && comparisonDistanceLabels.route_optimization.map((item, index) => (
          <DistanceLabel
            key={`optimized-distance-${index}`}
            position={item.position}
            distance={item.distance}
            isReturnSegment={false}
            segmentIndex={item.segmentIndex}
            backgroundColor={comparisonColors.route_optimization.main}
            pixelOffset={routeViewMode === "all" ? { x: 0, y: -25 } : undefined}
          />
        ))}

        {/* Distance Labels for Hybrid Shortest (comparison mode) */}
        {showShortestRoute && showDistanceLabels && comparisonDistanceLabels.hybrid_shortest.map((item, index) => (
          <DistanceLabel
            key={`shortest-distance-${index}`}
            position={item.position}
            distance={item.distance}
            isReturnSegment={false}
            segmentIndex={item.segmentIndex}
            backgroundColor={comparisonColors.hybrid_shortest.main}
            pixelOffset={routeViewMode === "all" ? { x: 0, y: 0 } : undefined}
          />
        ))}

        {/* Distance Labels for Nearest Neighbor (comparison mode) */}
        {showNearestNeighborRoute && showDistanceLabels && comparisonDistanceLabels.nearest_neighbor_shortest.map((item, index) => (
          <DistanceLabel
            key={`nearest-distance-${index}`}
            position={item.position}
            distance={item.distance}
            isReturnSegment={false}
            segmentIndex={item.segmentIndex}
            backgroundColor={comparisonColors.nearest_neighbor_shortest.main}
            pixelOffset={routeViewMode === "all" ? { x: 0, y: 25 } : undefined}
          />
        ))}

        {/* Store Markers - Support both stores array and single storeLocation */}
        {stores.length > 0
          ? stores.map((store) => {
              const isSelected = selectedStoreId === store.id;
              const markerIcon = isSelected
                ? SELECTED_STORE_MARKER_ICON
                : STORE_MARKER_ICON;
              const iconSize = isSelected
                ? new window.google.maps.Size(70, 88)
                : new window.google.maps.Size(60, 75);
              const iconAnchor = isSelected
                ? new window.google.maps.Point(35, 88)
                : new window.google.maps.Point(30, 75);

              return (
                <Marker
                  key={`store-${store.id}`}
                  position={store.location}
                  icon={{
                    url: markerIcon,
                    scaledSize: iconSize,
                    anchor: iconAnchor,
                  }}
                  title={store.name}
                  zIndex={isSelected ? 600 : 500}
                  onClick={() => handleStoreSelect(store.id)}
                  cursor="pointer"
                />
              );
            })
          : storeLocation && (
              <Marker
                position={storeLocation}
                icon={{
                  url: STORE_MARKER_ICON,
                  scaledSize: new window.google.maps.Size(60, 75),
                  anchor: new window.google.maps.Point(30, 75),
                }}
                title={t("store")}
                zIndex={500}
              />
            )}

        {/* Assigned Order Markers */}
        {sortedAssignedOrders.map((order, index) =>
          order.location ? (
            <OrderMarker
              key={`assigned-${order.id}`}
              position={order.location}
              order={order}
              markerStatus={getMarkerStatus(order, true)}
              sequence={index + 1}
              isSelected={selectedOrderId === order.id}
              isAssigned={true}
              showInfoWindow={showInfoWindowId === order.id}
              onSelect={() => handleOrderSelect(order.id)}
              onRemove={isEditable ? () => handleOrderRemove(order.id) : undefined}
              onCloseInfoWindow={handleCloseInfoWindow}
              zIndex={100 + index}
            />
          ) : null
        )}

        {/* Dashed lines from unassigned orders to nearest route point */}
        {unassignedOrdersWithRouteDistance.map((order) =>
          order.location && order.nearestPointOnRoute ? (
            <DashedPolyline
              key={`dashed-${order.id}`}
              path={[order.location, order.nearestPointOnRoute]}
              strokeColor={recommendedOrderIds.includes(order.id) ? "#52c41a" : "#8c8c8c"}
              strokeWeight={2}
              zIndex={5}
            />
          ) : null
        )}

        {/* Unassigned Order Markers */}
        {unassignedOrdersWithRouteDistance.map((order) =>
          order.location ? (
            <OrderMarker
              key={`unassigned-${order.id}`}
              position={order.location}
              order={order}
              markerStatus={getMarkerStatus(order, false)}
              isSelected={selectedOrderId === order.id}
              isAssigned={false}
              showInfoWindow={showInfoWindowId === order.id}
              onSelect={() => handleOrderSelect(order.id)}
              onAdd={isEditable ? () => handleOrderAdd(order.id) : undefined}
              onCloseInfoWindow={handleCloseInfoWindow}
              zIndex={recommendedOrderIds.includes(order.id) ? 50 : 10}
            />
          ) : null
        )}
      </GoogleMap>

      {/* Legend - Always visible */}
      <div className={styles.legend}>
        {/* Store icon - purple pin matching actual marker */}
        <div className={styles.legendItem}>
          <svg width="20" height="25" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 0C8.95 0 0 8.95 0 20C0 35 20 50 20 50S40 35 40 20C40 8.95 31.05 0 20 0Z" fill="#722ed1" stroke="#531dab" strokeWidth="2"/>
            <circle cx="20" cy="18" r="8" fill="white"/>
            <path d="M16 16h8v2H16v-2zm0 3h8v2H16v-2z" fill="#722ed1"/>
          </svg>
          <span>{t("store")}</span>
        </div>
        {/* Assigned order icon - blue pin with number */}
        <div className={styles.legendItem}>
          <svg width="20" height="25" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 0C8.95 0 0 8.95 0 20C0 35 20 50 20 50S40 35 40 20C40 8.95 31.05 0 20 0Z" fill="#3458a4" stroke="#283f7a" strokeWidth="2"/>
            <circle cx="20" cy="18" r="10" fill="white"/>
            <text x="20" y="22" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3458a4">1</text>
          </svg>
          <span>{t("orders_in_trip")}</span>
        </div>

        {/* Active Route Legend - Show when in active mode */}
        {showActiveRoute && (
          <>
            {/* Outbound route line - dynamic color based on status */}
            <div className={styles.legendItem}>
              <svg width="30" height="8" viewBox="0 0 30 8" xmlns="http://www.w3.org/2000/svg">
                <line x1="0" y1="4" x2="30" y2="4" stroke={routeColors[routeStatus].outbound} strokeWidth="4" strokeLinecap="round"/>
                <polygon points="25,0 30,4 25,8" fill={routeColors[routeStatus].outbound}/>
              </svg>
              <span>{t("outbound_route")}</span>
            </div>
            {/* Return route line - dashed, dynamic color based on status */}
            <div className={styles.legendItem}>
              <svg width="30" height="8" viewBox="0 0 30 8" xmlns="http://www.w3.org/2000/svg">
                <line x1="0" y1="4" x2="30" y2="4" stroke={routeColors[routeStatus].return} strokeWidth="3" strokeLinecap="round" strokeDasharray="4,3"/>
                <polygon points="25,1 30,4 25,7" fill={routeColors[routeStatus].return}/>
              </svg>
              <span>{t("return_route")}</span>
            </div>
          </>
        )}

        {/* Comparison Route Legend - Show when in comparison mode */}
        {showOptimizedRoute && (
          <div className={styles.legendItem}>
            <svg width="30" height="8" viewBox="0 0 30 8" xmlns="http://www.w3.org/2000/svg">
              <line x1="0" y1="4" x2="30" y2="4" stroke={comparisonColors.route_optimization.main} strokeWidth="4" strokeLinecap="round"/>
              <polygon points="25,0 30,4 25,8" fill={comparisonColors.route_optimization.main}/>
            </svg>
            <span>{t("optimized_route_label")}</span>
          </div>
        )}
        {showShortestRoute && (
          <div className={styles.legendItem}>
            <svg width="30" height="8" viewBox="0 0 30 8" xmlns="http://www.w3.org/2000/svg">
              <line x1="0" y1="4" x2="30" y2="4" stroke={comparisonColors.hybrid_shortest.main} strokeWidth="4" strokeLinecap="round"/>
              <polygon points="25,0 30,4 25,8" fill={comparisonColors.hybrid_shortest.main}/>
            </svg>
            <span>{t("shortest_route_label")}</span>
          </div>
        )}
        {showNearestNeighborRoute && (
          <div className={styles.legendItem}>
            <svg width="30" height="8" viewBox="0 0 30 8" xmlns="http://www.w3.org/2000/svg">
              <line x1="0" y1="4" x2="30" y2="4" stroke={comparisonColors.nearest_neighbor_shortest.main} strokeWidth="4" strokeLinecap="round"/>
              <polygon points="25,0 30,4 25,8" fill={comparisonColors.nearest_neighbor_shortest.main}/>
            </svg>
            <span>{t("strategy_nearest_neighbor")}</span>
          </div>
        )}

        {/* Available order icon - gray small pin */}
        <div className={styles.legendItem}>
          <svg width="14" height="18" viewBox="0 0 24 30" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 0C5.37 0 0 5.37 0 12C0 21 12 30 12 30S24 21 24 12C24 5.37 18.63 0 12 0Z" fill="#8c8c8c"/>
            <circle cx="12" cy="11" r="4" fill="white"/>
          </svg>
          <span>{t("available_orders")}</span>
        </div>
        {/* Recommended icon - green small pin */}
        <div className={styles.legendItem}>
          <svg width="14" height="18" viewBox="0 0 24 30" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 0C5.37 0 0 5.37 0 12C0 21 12 30 12 30S24 21 24 12C24 5.37 18.63 0 12 0Z" fill="#52c41a"/>
            <circle cx="12" cy="11" r="4" fill="white"/>
          </svg>
          <span>{t("recommended")}</span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TripRouteMap);
